import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Zap, Image as ImageIcon, Video, Box, FileImage, Info, Activity, Clock, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PerformanceSettings {
  lazyLoadImages: boolean;
  lazyLoadVideo: boolean;
  lazyLoad3DModel: boolean;
  videoPreloadMetadata: boolean;
  imageLazyNative: boolean;
  webVitalsMonitoring: boolean;
}

interface WebVitalsData {
  lcp: number | null;
  fid: number | null;
  cls: number | null;
}

const AdminPerformance = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<PerformanceSettings>({
    lazyLoadImages: false,
    lazyLoadVideo: false,
    lazyLoad3DModel: false,
    videoPreloadMetadata: true,
    imageLazyNative: false,
    webVitalsMonitoring: false,
  });

  const [webVitals, setWebVitals] = useState<WebVitalsData>({
    lcp: null,
    fid: null,
    cls: null,
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem("performance-settings");
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }

    let active = true;
    void supabase
      .from("tracking_settings")
      .select("performance_monitoring")
      .eq("singleton_key", true)
      .single()
      .then(({ data, error }) => {
        if (active && !error && data?.performance_monitoring) measureWebVitals();
      });

    return () => { active = false; };
  }, []);

  const measureWebVitals = () => {
    try {
      // LCP - Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        const value = lastEntry.renderTime || lastEntry.loadTime;
        setWebVitals(prev => ({ ...prev, lcp: Math.round(value) }));
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

      // FID - First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          const value = entry.processingStart - entry.startTime;
          setWebVitals(prev => ({ ...prev, fid: Math.round(value) }));
        });
      });
      fidObserver.observe({ type: "first-input", buffered: true });

      // CLS - Cumulative Layout Shift
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput && entry.value) {
            clsValue += entry.value;
          }
        });
        setWebVitals(prev => ({ ...prev, cls: Math.round(clsValue * 1000) / 1000 }));
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });
    } catch (error) {
      console.error("Error measuring web vitals:", error);
    }
  };

  const handleToggle = (key: keyof PerformanceSettings) => {
    const newSettings = {
      ...settings,
      [key]: !settings[key],
    };
    setSettings(newSettings);
  };

  const handleSave = () => {
    localStorage.setItem("performance-settings", JSON.stringify(settings));
    toast({
      title: "Einstellungen gespeichert",
      description: "Die Performance-Optimierungen wurden aktualisiert. Seite neu laden, um die Änderungen zu sehen.",
    });
  };

  const optimizationItems = [
    {
      id: "lazyLoadImages" as keyof PerformanceSettings,
      title: "Lazy Loading für Bilder",
      description: "Bilder werden erst geladen, wenn sie im Viewport sichtbar werden",
      icon: ImageIcon,
      impact: "Mittel",
      category: "images",
    },
    {
      id: "imageLazyNative" as keyof PerformanceSettings,
      title: "Native Browser Lazy Loading (loading='lazy')",
      description: "Verwendet das native loading='lazy' Attribut für <img> Tags",
      icon: FileImage,
      impact: "Hoch",
      category: "images",
    },
    {
      id: "lazyLoadVideo" as keyof PerformanceSettings,
      title: "Lazy Loading für Video",
      description: "Video-Komponente wird erst geladen, wenn benötigt",
      icon: Video,
      impact: "Hoch",
      category: "media",
    },
    {
      id: "videoPreloadMetadata" as keyof PerformanceSettings,
      title: "Video Preload Metadata",
      description: "Nur Metadaten des Videos vorladen, nicht das gesamte Video",
      icon: Video,
      impact: "Mittel",
      category: "media",
    },
    {
      id: "lazyLoad3DModel" as keyof PerformanceSettings,
      title: "Lazy Loading für 3D Model",
      description: "3D-Viewer wird erst geladen, wenn der Benutzer scrollt",
      icon: Box,
      impact: "Sehr Hoch",
      category: "3d",
    },
    {
      id: "webVitalsMonitoring" as keyof PerformanceSettings,
      title: "Web Vitals Monitoring",
      description: "Erfasst Core Web Vitals (LCP, FID, CLS) und sendet sie an Analytics",
      icon: Zap,
      impact: "Niedrig",
      category: "monitoring",
    },
  ];

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case "Sehr Hoch":
        return "text-destructive";
      case "Hoch":
        return "text-orange-500";
      case "Mittel":
        return "text-yellow-500";
      default:
        return "text-muted-foreground";
    }
  };

  const getVitalStatus = (metric: string, value: number | null) => {
    if (value === null) return { status: "Warten...", color: "text-muted-foreground" };
    
    if (metric === "lcp") {
      if (value <= 2500) return { status: "Gut", color: "text-green-500" };
      if (value <= 4000) return { status: "Verbesserung nötig", color: "text-yellow-500" };
      return { status: "Schlecht", color: "text-destructive" };
    }
    if (metric === "fid") {
      if (value <= 100) return { status: "Gut", color: "text-green-500" };
      if (value <= 300) return { status: "Verbesserung nötig", color: "text-yellow-500" };
      return { status: "Schlecht", color: "text-destructive" };
    }
    if (metric === "cls") {
      if (value <= 0.1) return { status: "Gut", color: "text-green-500" };
      if (value <= 0.25) return { status: "Verbesserung nötig", color: "text-yellow-500" };
      return { status: "Schlecht", color: "text-destructive" };
    }
    return { status: "Unbekannt", color: "text-muted-foreground" };
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-background/95">
      <Navigation />
      <AdminNavigation />
      
      <main className="flex-1 container mx-auto px-4 pt-20 pb-16">
        <Breadcrumbs items={[
          { label: "Admin", href: "/admin/kalender" },
          { label: "Performance" }
        ]} />
        
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="break-words text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              Performance-Optimierung
            </h1>
            <p className="text-muted-foreground text-lg">
              Steuere alle Performance-Optimierungen für deine Website manuell
            </p>
          </div>

          <Alert className="mb-8 border-primary/50">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Wichtig:</strong> Nach dem Speichern muss die Seite neu geladen werden, damit die Änderungen wirksam werden.
            </AlertDescription>
          </Alert>

          {/* Web Vitals Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span>LCP (Largest Contentful Paint)</span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-1">
                  {webVitals.lcp !== null ? `${webVitals.lcp}ms` : "—"}
                </div>
                <div className={`text-sm ${getVitalStatus("lcp", webVitals.lcp).color}`}>
                  {getVitalStatus("lcp", webVitals.lcp).status}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Ziel: {'<'} 2.5s | Gut: {'<'} 2.5s | Schlecht: {'>'} 4.0s
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Activity className="h-4 w-4 text-primary" />
                    <span>FID (First Input Delay)</span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-1">
                  {webVitals.fid !== null ? `${webVitals.fid}ms` : "—"}
                </div>
                <div className={`text-sm ${getVitalStatus("fid", webVitals.fid).color}`}>
                  {getVitalStatus("fid", webVitals.fid).status}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Ziel: {'<'} 100ms | Gut: {'<'} 100ms | Schlecht: {'>'} 300ms
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span>CLS (Cumulative Layout Shift)</span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-1">
                  {webVitals.cls !== null ? webVitals.cls.toFixed(3) : "—"}
                </div>
                <div className={`text-sm ${getVitalStatus("cls", webVitals.cls).color}`}>
                  {getVitalStatus("cls", webVitals.cls).status}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Ziel: {'<'} 0.1 | Gut: {'<'} 0.1 | Schlecht: {'>'} 0.25
                </p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="all" className="space-y-6">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto p-1 [scrollbar-width:thin]">
              <TabsTrigger value="all" className="shrink-0">Alle</TabsTrigger>
              <TabsTrigger value="images" className="shrink-0">Bilder</TabsTrigger>
              <TabsTrigger value="media" className="shrink-0">Video</TabsTrigger>
              <TabsTrigger value="3d" className="shrink-0">3D Model</TabsTrigger>
              <TabsTrigger value="monitoring" className="shrink-0">Monitoring</TabsTrigger>
            </TabsList>

            {["all", "images", "media", "3d", "monitoring"].map((category) => (
              <TabsContent key={category} value={category} className="space-y-4">
                {optimizationItems
                  .filter((item) => category === "all" || item.category === category)
                  .map((item) => {
                    const Icon = item.icon;
                    return (
                      <Card key={item.id} className="hover:shadow-elegant transition-shadow">
                        <CardHeader>
                           <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
                             <div className="flex min-w-0 items-start space-x-4">
                              <div className="p-3 bg-primary/10 rounded-lg">
                                <Icon className="h-6 w-6 text-primary" />
                              </div>
                               <div className="min-w-0">
                                <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                                <CardDescription className="text-base">
                                  {item.description}
                                </CardDescription>
                                <div className="mt-2 flex items-center space-x-2">
                                  <span className="text-sm text-muted-foreground">Impact:</span>
                                  <span className={`text-sm font-medium ${getImpactColor(item.impact)}`}>
                                    {item.impact}
                                  </span>
                                </div>
                              </div>
                            </div>
                             <div className="flex shrink-0 items-center space-x-2">
                              <Switch
                                id={item.id}
                                checked={settings[item.id]}
                                onCheckedChange={() => handleToggle(item.id)}
                              />
                              <Label
                                htmlFor={item.id}
                                className="text-sm font-medium cursor-pointer"
                              >
                                {settings[item.id] ? "Aktiv" : "Inaktiv"}
                              </Label>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    );
                  })}
              </TabsContent>
            ))}
          </Tabs>

          <div className="mt-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="text-sm text-muted-foreground">
              {Object.values(settings).filter(Boolean).length} von {optimizationItems.length} Optimierungen aktiv
            </div>
            <Button
              onClick={handleSave}
              size="lg"
              className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
            >
              <Zap className="w-4 h-4 mr-2" />
              Einstellungen speichern
            </Button>
          </div>

          <Card className="mt-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Info className="h-5 w-5 text-primary" />
                <span>Performance-Tipps</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Empfohlene Einstellungen:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>3D Model Lazy Loading: Immer aktivieren (sehr große Datei)</li>
                  <li>Video Lazy Loading: Aktivieren für bessere Initial Load Zeit</li>
                  <li>Native Image Lazy Loading: Kann zu Problemen führen, vorsichtig testen</li>
                  <li>Video Preload Metadata: Aktivieren für schnelleren Start</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Testing:</h3>
                <p className="text-sm text-muted-foreground">
                  Teste die Performance mit Google PageSpeed Insights nach jeder Änderung. 
                  Aktiviere die Optimierungen schrittweise und beobachte die Auswirkungen.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminPerformance;
